import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../components-v2/Button';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '../../../components-v2/Collapsible';
import { CountBadge } from '../../../components-v2/CountBadge';
import { THEME } from '../../../components-v2/Theme/theme';
import { ChevronDown } from '../../../components/svgs/icons/ChevronDown';
import { PlusIcon2 } from '../../../components/svgs/icons/PlusIcon';
import { ActionItemBucket, BUCKET_LABELS } from '../utils/buckets';
import { ACTION_ITEM_DRAG_TYPE } from './ActionItemRow';

export const BUCKET_DOT_COLORS: Record<ActionItemBucket, string> = {
	overdue: THEME.colors.danger.main,
	today: THEME.colors.primary.main,
	tomorrow: THEME.colors.secondary.main,
	thisWeek: THEME.colors.text.light,
	later: THEME.colors.text.light,
	noDate: THEME.colors.border.main,
};

interface IProps {
	bucket: ActionItemBucket;
	count: number;
	collapsed: boolean;
	onToggleCollapsed: () => void;
	droppable: boolean;
	/** True while any action item is being dragged; empty sections open up as drop zones. */
	dragging: boolean;
	onAdd?: () => void;
	children: React.ReactNode;
}

export const ActionItemSection: React.FC<IProps> = ({
	bucket,
	count,
	collapsed,
	onToggleCollapsed,
	droppable,
	dragging,
	onAdd,
	children,
}) => {
	const sectionRef = React.useRef<HTMLElement>(null);
	const [draggedOver, setDraggedOver] = React.useState(false);

	React.useEffect(() => {
		const element = sectionRef.current;
		if (!element || !droppable) {
			return;
		}
		return dropTargetForElements({
			element,
			canDrop: ({ source }) => source.data.type === ACTION_ITEM_DRAG_TYPE,
			getData: () => ({ bucket }),
			onDragEnter: () => setDraggedOver(true),
			onDragLeave: () => setDraggedOver(false),
			onDrop: () => setDraggedOver(false),
		});
	}, [bucket, droppable]);

	const showDropZone = count === 0 && droppable && dragging;

	return (
		<section
			ref={sectionRef}
			data-testid={`action-item-section-${bucket}`}
			className={css(styleSheet.section, draggedOver && styleSheet.sectionDraggedOver)}
		>
			<Collapsible open={!collapsed || dragging} onOpenChange={onToggleCollapsed}>
				<div className={css(styleSheet.headerRow)}>
					<CollapsibleTrigger className={css(styleSheet.header)}>
						<ChevronDown
							className={css(styleSheet.chevron, collapsed && !dragging && styleSheet.chevronCollapsed)}
							width={14}
							height={14}
						/>
						<span className={css(styleSheet.dot)} style={{ backgroundColor: BUCKET_DOT_COLORS[bucket] }} aria-hidden />
						<span className={css(styleSheet.label, bucket === 'overdue' && count > 0 && styleSheet.labelOverdue)}>
							{BUCKET_LABELS[bucket]}
						</span>
						{count > 0 ? <CountBadge count={count} max={99} styles={[styleSheet.count]} /> : null}
					</CollapsibleTrigger>
					{onAdd ? (
						<Button
							variant='ghost'
							size='sm'
							type='button'
							aria-label={`Add an action item for ${BUCKET_LABELS[bucket].toLowerCase()}`}
							data-slot='section-add'
							styles={[styleSheet.addButton]}
							onClick={onAdd}
						>
							<PlusIcon2 width={12} height={12} />
							<span>Add</span>
						</Button>
					) : null}
				</div>
				<CollapsiblePanel>
					{showDropZone ? (
						<div className={css(styleSheet.dropZone, draggedOver && styleSheet.dropZoneActive)}>
							Drop to move to {BUCKET_LABELS[bucket].toLowerCase()}
						</div>
					) : count > 0 ? (
						<div className={css(styleSheet.card, draggedOver && styleSheet.cardDraggedOver)}>{children}</div>
					) : (
						children
					)}
				</CollapsiblePanel>
			</Collapsible>
		</section>
	);
};

const styleSheet = StyleSheet.create({
	section: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		paddingBottom: THEME.spacing.sm,
	},
	sectionDraggedOver: {},
	headerRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		':hover [data-slot="section-add"]': {
			opacity: 1,
		},
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		flex: 1,
		minWidth: 0,
		background: 'none',
		border: 'none',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.xs}px`,
		cursor: 'pointer',
		textAlign: 'left',
		color: THEME.colors.text.main,
	},
	chevron: {
		color: THEME.colors.icon.gray,
		transition: 'transform 150ms ease-out',
	},
	chevronCollapsed: {
		transform: 'rotate(-90deg)',
	},
	dot: {
		width: 8,
		height: 8,
		borderRadius: '50%',
		flexShrink: 0,
	},
	label: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	labelOverdue: {
		color: THEME.colors.danger.dark,
	},
	count: {
		backgroundColor: THEME.colors.background.main,
		color: THEME.colors.text.light,
	},
	addButton: {
		opacity: 0,
		transition: 'opacity 120ms ease-out',
		color: THEME.colors.text.light,
		':focus-visible': {
			opacity: 1,
		},
	},
	card: {
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		overflow: 'hidden',
		transition: 'border-color 120ms ease-out, box-shadow 120ms ease-out',
	},
	cardDraggedOver: {
		borderColor: THEME.colors.primary.main,
		boxShadow: `0 0 0 3px ${THEME.colors.primary.light}`,
	},
	dropZone: {
		padding: `${THEME.spacing.md}px`,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
		textAlign: 'center',
		border: `1px dashed ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		transition: 'border-color 120ms ease-out, background-color 120ms ease-out',
	},
	dropZoneActive: {
		borderColor: THEME.colors.primary.main,
		backgroundColor: THEME.colors.primary.light,
		color: THEME.colors.text.main,
	},
});
